/**
 * inlineRows — keeping an Inline's one-row grid true to its item.
 *
 * An Inline ("inline-grid") is a grid item whose nested grid is ONE row of
 * `item.position.w` columns. Three facts must follow the item without the
 * author doing anything:
 *
 *  - the children sit packed at the Inline's `justify` (start/center/end) —
 *    `alignInlineRow` after every commit to the row (drop, move, remove,
 *    resize);
 *  - the nested grid's `columns` equal the Inline's width, so resizing the
 *    Inline resizes its row (children keep their `w`);
 *  - the Inline cannot be resized narrower than its children's total width
 *    (`constraints.minW`), and its height is its tallest child's (`minH` =
 *    `maxH`), so any block — a two-row image too — fits its one row.
 *
 * Desktop only: at the tablet and mobile breakpoints y-grid re-seeds every
 * bound grid with its auto-stack (children full width, stacked), which is
 * not a row. Reconciling there wrote that stack back as `::bp:` overrides
 * and a wrong `minW` on the shared item, so it waits for desktop.
 * holds-until: an Inline has its own tablet/mobile layout (today it stacks
 * like any grid there, and a drop made off desktop is aligned on return).
 *
 * `syncInlineRows` is the store subscriber that applies all three (installed
 * once, from usePageNode's interaction wiring); it waits for the gesture to
 * end so a live drag keeps its engine reflow. The functions are plain helpers
 * over the store so the selection bar can re-align on demand.
 */
import { subscribeGridsCommitted, useGridStore } from "@fredericrous/grid";
import { getActiveBreakpoint } from "@website-builder/y-grid";
import { alignInlineRow, type InlineJustify } from "@website-builder/schemas";

export const INLINE_GRID = "inline-grid";

const JUSTIFIES: readonly InlineJustify[] = ["start", "center", "end"];

/** A container item (section or Inline) and the grid it hosts. */
export interface NestedContainer {
  readonly nestedGridId: string;
  readonly hostGridId: string;
  readonly hostItemId: string;
  readonly contentType: string;
}

/** Every nested grid under `rootGridId`, at every depth (page → section →
 *  Inline), parents before children. Reads only items that carry a
 *  `nestedGridId` — the field the engine and y-grid persist. */
export function nestedContainers(
  grids: Readonly<
    Record<
      string,
      | {
          items: Readonly<
            Record<string, { contentType: string; nestedGridId?: string }>
          >;
        }
      | undefined
    >
  >,
  rootGridId: string,
): NestedContainer[] {
  const out: NestedContainer[] = [];
  const seen = new Set<string>([rootGridId]);
  const queue = [rootGridId];
  while (queue.length > 0) {
    const gid = queue.shift()!;
    for (const [itemId, it] of Object.entries(grids[gid]?.items ?? {})) {
      const nid = it.nestedGridId;
      if (typeof nid !== "string" || seen.has(nid)) continue;
      seen.add(nid);
      out.push({
        nestedGridId: nid,
        hostGridId: gid,
        hostItemId: itemId,
        contentType: it.contentType,
      });
      queue.push(nid);
    }
  }
  return out;
}

/** The Inline's justify, "start" when unset or unknown. */
export function justifyOf(
  contentProps: Record<string, unknown> | undefined,
): InlineJustify {
  const j = contentProps?.["justify"];
  return JUSTIFIES.find((k) => k === j) ?? "start";
}

/** Move the packed group of an Inline's grid to its justify. No-op when the
 *  grid isn't an Inline's, or the children already sit there. */
export function alignInlineGrid(gridId: string): void {
  const st = useGridStore.getState();
  const grid = st.grids[gridId];
  if (!grid?.parentGridId || !grid.parentItemId) return;
  const host = st.grids[grid.parentGridId]?.items[grid.parentItemId];
  if (host?.contentType !== INLINE_GRID) return;
  const aligned = alignInlineRow(
    Object.values(grid.items).map((it) => ({
      id: it.id,
      x: it.position.x,
      w: it.position.w,
    })),
    grid.config.columns,
    justifyOf(host.contentProps as Record<string, unknown> | undefined),
  );
  for (const a of aligned) {
    const it = grid.items[a.id]!;
    if (it.position.x !== a.x || it.position.y !== 0)
      st.updateItem(gridId, a.id, {
        position: { ...it.position, x: a.x, y: 0 },
      });
  }
}

/** Reconcile every Inline's grid with its item: columns, minW, alignment.
 *
 *  For a change that came FROM Yjs — an undo, a redo, a collaborator's
 *  edit — use {@link correctInlineColumns} instead: the side that made it
 *  already wrote the constraints and alignment, and writing them again here
 *  would be a new local transaction, which empties the redo stack (an undo
 *  of a drop into an Inline could not be redone). */
export function reconcileInlineRows(): void {
  reconcile(true);
}

/** The store-only half of {@link reconcileInlineRows}: each Inline's row
 *  `columns` follows its width (y-grid does not store `columns`). Used for
 *  a change that came from Yjs, see below. */
export function correctInlineColumns(): void {
  reconcile(false);
}

function reconcile(persist: boolean): void {
  const st = useGridStore.getState();
  if (st.interaction.gesture.kind !== "idle") return;
  if (getActiveBreakpoint() !== "desktop") return;
  for (const [gid, grid] of Object.entries(st.grids)) {
    for (const host of Object.values(grid.items)) {
      if (host.contentType !== INLINE_GRID || !host.nestedGridId) continue;
      const row = st.grids[host.nestedGridId];
      if (!row) continue;
      if (row.config.columns !== host.position.w)
        st.updateGridConfig(host.nestedGridId, { columns: host.position.w });
      if (!persist) continue;
      const used = Object.values(row.items).reduce(
        (n, it) => n + it.position.w,
        0,
      );
      const minW = Math.max(1, used);
      const rows = Object.values(row.items).reduce(
        (n, it) => Math.max(n, it.position.h),
        1,
      );
      const c = host.constraints;
      if (c.minW !== minW || c.minH !== rows || c.maxH !== rows)
        st.updateItem(gid, host.id, {
          constraints: { ...c, minW, minH: rows, maxH: rows },
        });
      alignInlineGrid(host.nestedGridId);
    }
  }
}

let installed = false;

/** Subscribe once: re-run the reconcile whenever grid contents or the live
 *  gesture change. Idempotent updates, so the writes it makes settle at once. */
export function syncInlineRows(): void {
  if (installed) return;
  installed = true;
  // Driven by the store's commit events, which say where a change came from
  // (a store subscriber alone cannot), and by a gesture ending. Deferred to
  // a microtask so the store has settled; a batch that includes a change
  // from Yjs does not persist (see reconcileInlineRows).
  let pending: { persist: boolean } | null = null;
  const schedule = (persist: boolean) => {
    if (pending) {
      pending.persist &&= persist;
      return;
    }
    pending = { persist };
    queueMicrotask(() => {
      const run = pending!;
      pending = null;
      if (run.persist) reconcileInlineRows();
      else correctInlineColumns();
    });
  };
  subscribeGridsCommitted(useGridStore, (event) => {
    schedule(event.origin !== "remote");
  });
  useGridStore.subscribe((st, prev) => {
    if (
      st.interaction !== prev.interaction &&
      st.interaction.gesture.kind === "idle"
    )
      schedule(true);
  });
}
